import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Tour from "reactour";
import {
  APP_TOUR_MAIN_STEPS,
  APP_TOUR_ORG_STEPS,
} from "../../constants/appTour";
import { setDemoTourFinished } from "../../store/common";
import {
  isOrgTourDismissed,
  setOrgTourDismissed,
} from "../../utils/appTourStorage";
import { updateDemoTour } from "../../utils/organization";

const stepStyle = {
  color: "#1f2937",
  background: "#eff6ff",
  maxWidth: "22rem",
  lineHeight: 1.5,
};

/** Match subscribe modal accents: Xero sky, QBO green */
const XERO_TOUR_CLASS = "font-semibold text-sky-600";
const QBO_TOUR_CLASS = "font-semibold text-green-600";

const PLATFORM_NAME_PATTERN =
  /(QuickBooks Online|QuickBooks|\bQBO\b|\bXero\b)/gi;

function isQboPlatformLabel(label) {
  const lower = String(label).toLowerCase();
  return lower.includes("quickbooks") || lower === "qbo";
}

function highlightPlatformNames(text) {
  const str = String(text ?? "");
  if (!str) return null;

  const parts = [];
  let lastIndex = 0;
  let match;
  const re = new RegExp(PLATFORM_NAME_PATTERN.source, PLATFORM_NAME_PATTERN.flags);

  while ((match = re.exec(str)) !== null) {
    if (match.index > lastIndex) {
      parts.push(str.slice(lastIndex, match.index));
    }
    const label = match[0];
    parts.push(
      <span
        key={`${match.index}-${label}`}
        className={
          isQboPlatformLabel(label) ? QBO_TOUR_CLASS : XERO_TOUR_CLASS
        }
      >
        {label}
      </span>
    );
    lastIndex = re.lastIndex;
  }

  if (lastIndex < str.length) {
    parts.push(str.slice(lastIndex));
  }

  return parts.length > 0 ? parts : str;
}

function tourStepContent(title, body) {
  return (
    <div>
      <p className="m-0 mb-2 text-base font-semibold text-gray-900">
        {highlightPlatformNames(title)}
      </p>
      <p className="m-0 text-sm leading-relaxed text-gray-700">
        {highlightPlatformNames(body)}
      </p>
    </div>
  );
}

function buildTourSteps(stepDefs) {
  if (typeof document === "undefined") return [];

  return stepDefs
    .filter((def) => Boolean(document.querySelector(def.selector)))
    .map((def) => ({
      selector: def.selector,
      content: tourStepContent(def.title, def.body),
      style: stepStyle,
    }));
}

function tourUserKey(user) {
  return user?.sub || user?.email || "";
}

function WebTour() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { getIdTokenClaims, user: auth0User } = useAuth0();
  const demoTourFinished = useSelector(
    (state) => state.commonReducer.demoTourFinished
  );
  const orgCount = useSelector((state) => state.commonReducer.accountCounts);

  const onOrganizationsPage = location.pathname.startsWith("/organizations");
  const userKey = tourUserKey(auth0User);

  const [orgTourDismissed, setOrgTourDismissedState] = useState(() =>
    isOrgTourDismissed(userKey)
  );

  useEffect(() => {
    setOrgTourDismissedState(isOrgTourDismissed(userKey));
  }, [userKey]);

  const tourPhase = useMemo(() => {
    if (!onOrganizationsPage) return null;
    if (!demoTourFinished) return "main";
    if (orgCount > 0 && !orgTourDismissed) return "org";
    return null;
  }, [
    onOrganizationsPage,
    demoTourFinished,
    orgCount,
    orgTourDismissed,
  ]);

  const [steps, setSteps] = useState([]);
  const [stepsReady, setStepsReady] = useState(false);

  useEffect(() => {
    if (!tourPhase) {
      setSteps([]);
      setStepsReady(false);
      return undefined;
    }

    setStepsReady(false);
    const stepDefs =
      tourPhase === "main" ? APP_TOUR_MAIN_STEPS : APP_TOUR_ORG_STEPS;

    const resolveSteps = () => {
      const built = buildTourSteps(stepDefs);
      setSteps(built);
      setStepsReady(built.length > 0);
    };

    resolveSteps();
    const timer = window.setTimeout(resolveSteps, 500);

    return () => window.clearTimeout(timer);
  }, [tourPhase, orgCount]);

  const persistMainTourFinished = async () => {
    dispatch(setDemoTourFinished(true));
    try {
      const token = await getIdTokenClaims();
      if (token?.__raw) {
        await updateDemoTour(token.__raw);
      }
    } catch (_) {
      /* keep local dismiss even if API fails */
    }
  };

  const persistOrgTourFinished = () => {
    setOrgTourDismissed(userKey);
    setOrgTourDismissedState(true);
  };

  const handleCloseTour = () => {
    if (tourPhase === "main") {
      persistMainTourFinished();
      return;
    }
    if (tourPhase === "org") {
      persistOrgTourFinished();
    }
  };

  const onFinish = () => {
    handleCloseTour();
  };

  const showTour = Boolean(tourPhase) && stepsReady && steps.length > 0;

  const stepCountLabel = useMemo(
    () => `${steps.length} step${steps.length === 1 ? "" : "s"}`,
    [steps.length]
  );

  const tourLabel =
    tourPhase === "org"
      ? `Organization actions tour, ${stepCountLabel}`
      : `WOWzer app guided tour, ${stepCountLabel}`;

  const finishLabel =
    tourPhase === "org" ? "Finish" : "Finish tour";

  return (
    <Tour
      steps={steps}
      isOpen={showTour}
      rounded={8}
      stepInteraction={true}
      showNavigation={true}
      showButtons={true}
      showCloseButton={true}
      showNumber={true}
      accentColor="#2563EB"
      maskColor="rgba(15, 23, 42, 0.45)"
      onRequestClose={handleCloseTour}
      lastStepNextButton={
        <Button type="primary" onClick={onFinish}>
          {finishLabel}
        </Button>
      }
      aria-label={tourLabel}
    />
  );
}

export default WebTour;
